/**
 * Google Identity Services, loaded from
 * https://accounts.google.com/gsi/client and asked to draw its own button.
 *
 * Google owns every pixel inside the container, which is how the button stays
 * inside Google's branding rules without this repo shipping a logo or a font.
 *
 * A cancellation never reaches the callback. Closing Google's account chooser
 * simply does nothing, so there is no cancel branch anywhere in this file.
 */

import { loadExternalScript } from "./scriptLoader";

type typeGoogleCredentialResponse = { credential?: string };

type typeGoogleIdApi = {
  initialize: (config: {
    client_id: string;
    callback: (response: typeGoogleCredentialResponse) => void;
    auto_select: boolean;
    cancel_on_tap_outside: boolean;
    itp_support: boolean;
    use_fedcm_for_button: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type: "standard";
      theme: typeGoogleButtonTheme;
      size: "large";
      text: "continue_with";
      shape: "rectangular";
      logo_alignment: "left";
      width: string;
    },
  ) => void;
};

declare global {
  // An `interface` is the only thing TypeScript merges into the built-in
  // `Window`, so the repo's type-over-interface rule cannot apply here.
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Window {
    google?: { accounts?: { id?: typeGoogleIdApi } };
  }
}

export const GOOGLE_GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

export type typeGoogleButtonTheme = "outline" | "outline_dark";

export type typeRenderGoogleSignInButtonArgs = {
  /** The element Google draws into. Emptied before Google is asked to draw. */
  container: HTMLElement;
  /** `google.webClientId` from POST /oauth/provider-config-get. Never a bundle id. */
  webClientId: string;
  theme: typeGoogleButtonTheme;
  /** From clampProviderButtonWidth. Google caps this at 400 internally. */
  widthPx: number;
  onIdToken: (args: { idToken: string }) => void;
};

/**
 * Rejects when the script will not load or Google's API is absent after it
 * does. A caller treats that as "render no button", never as an error modal.
 */
export async function renderGoogleSignInButton({
  container,
  webClientId,
  theme,
  widthPx,
  onIdToken,
}: typeRenderGoogleSignInButtonArgs): Promise<void> {
  await loadExternalScript({ src: GOOGLE_GSI_SCRIPT_SRC });

  const googleId = window.google?.accounts?.id;
  if (!googleId) {
    throw new Error(
      "Google Identity Services loaded but window.google.accounts.id is missing",
    );
  }

  googleId.initialize({
    client_id: webClientId,
    callback: (response) => {
      // No cancel branch on purpose: Google never calls this when a fan closes
      // the chooser, so the screen is left exactly as it was by construction.
      if (
        typeof response.credential === "string" &&
        response.credential.length > 0
      ) {
        onIdToken({ idToken: response.credential });
      }
    },
    auto_select: false,
    cancel_on_tap_outside: true,
    itp_support: true,
    use_fedcm_for_button: true,
  });

  // `renderButton` appends, so a second render without this stacks two buttons.
  container.replaceChildren();

  googleId.renderButton(container, {
    type: "standard",
    theme,
    size: "large",
    text: "continue_with",
    shape: "rectangular",
    logo_alignment: "left",
    width: String(widthPx),
  });
}
